import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { BackgroundJobsProvider } from './contexts/BackgroundJobsContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { DatabaseConnectionToast } from './components/DatabaseConnectionToast';
import Layout from './components/Layout';
import { AuthPage } from './pages/AuthPage';
import { Dashboard } from './pages/Dashboard';
import { LandingPage } from './pages/LandingPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { UserProfile } from './pages/UserProfile';
import { AuthTestPage } from './pages/AuthTestPage';
import { FamilyManagement } from './pages/FamilyManagement';
import BalanceManagement from './components/BalanceManagement';
import ManualBalanceManagement from './components/ManualBalanceManagement';
import CalendarViewTable from './components/CalendarViewTable';
import BookingDetail from './pages/BookingDetail';
import { BankAccounts } from './pages/BankAccounts';
import { Loans } from './pages/Loans';
import { Properties } from './pages/Properties';
import { Entities } from './pages/Entities';
import { Pensions } from './pages/Pensions';
import { HostExpenses } from './pages/HostExpenses';
import { Tax } from './pages/Tax';
import VAT from './pages/Vat';
import AnnualReturns from './pages/AnnualReturns';
import Performance from './pages/Performance';
import { TaxReturns } from './pages/TaxReturns';
import { LoansSchedule } from './pages/LoansSchedule';
import GeneralLedger from './pages/GeneralLedger';
import RBACManagement from './pages/RBACManagement';
import DatabaseOverview from './pages/DatabaseOverview';
import TransactionComparison from './pages/TransactionComparison';
import Revolut from './pages/Revolut';
import ReceiptsInvoices from './pages/ReceiptsInvoices';
import PandleTransactions from './pages/PandleTransactions';
import PersonalFinances from './pages/PersonalFinances';
import TransactionClassificationReview from './pages/TransactionClassificationReview';
import { Settings } from './pages/Settings';

function App() {
  // Frontend upgrade to Starter plan - fix routing issues
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <BackgroundJobsProvider>
          <DatabaseConnectionToast />
          <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth-test" element={<AuthTestPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route 
              path="/families" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <FamilyManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserProfile />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              } 
            />
                <Route 
                  path="/accounts" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <BankAccounts />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/loans" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Loans />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/properties" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Properties />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
            <Route 
              path="/balances" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <BalanceManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/balances-manual" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ManualBalanceManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/bookings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CalendarViewTable />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/bookings/:bookingId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookingDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Redirect old /calendar route to /bookings */}
            <Route 
              path="/calendar" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <CalendarViewTable />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/entities" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Entities />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/entities/properties" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Entities />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/entities/loans" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Entities />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/entities/accounts" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Entities />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/entities/companies" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Entities />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/entities/pensions"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Entities />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/entities/income" element={<Navigate to="/tax" replace />} />
            <Route 
              path="/entities/users" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Entities />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/entities/families" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Entities />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route path="/host-expenses" element={<Navigate to="/hosting/expenses" replace />} />
            <Route path="/hosting" element={<Navigate to="/hosting/calculator" replace />} />
            <Route 
              path="/hosting/expenses" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <HostExpenses />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/hosting/calculator" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <HostExpenses />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/hosting/niall-calcs" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <HostExpenses />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/hosting/niall-account"
              element={<Navigate to="/hosting/niall-vrbo-account" replace />}
            />
            <Route
              path="/hosting/niall-vrbo-account"
              element={
                <ProtectedRoute>
                  <Layout>
                    <HostExpenses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/tax" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Tax />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/pensions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Pensions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/vat" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <VAT />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/annual-returns"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AnnualReturns />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/performance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Performance />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/tax-returns" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaxReturns />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gl-transactions" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <GeneralLedger />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/loans-schedule" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <LoansSchedule />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            {/* New unified Revolut page with tabs */}
            <Route 
              path="/revolut" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Revolut />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            {/* Redirect old routes to new Revolut page with appropriate tabs */}
            <Route 
              path="/transactions" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Revolut />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/receipts-invoices" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReceiptsInvoices />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rbac" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <RBACManagement defaultTab={0} />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rbac/entities" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <RBACManagement defaultTab={0} />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rbac/pages" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <RBACManagement defaultTab={1} />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/database-overview" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <DatabaseOverview />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/transaction-comparison" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <TransactionComparison />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            {/* Redirect old detailed/unified routes to main page with appropriate tab */}
            <Route
              path="/transaction-comparison-detailed"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <TransactionComparison />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transaction-comparison-unified"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <TransactionComparison />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Redirect old Revolut routes to new unified page with appropriate tabs */}
            <Route 
              path="/webhooks" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Revolut />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/revolut-expenses" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Revolut />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/revolut-counterparties" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <Revolut />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/transaction-classification-review" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <TransactionClassificationReview />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/personal-finances" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <PersonalFinances />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/pandle-transactions" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <PandleTransactions />
                  </Layout>
                </ProtectedRoute>
              } 
            />
          </Routes>
          </BackgroundJobsProvider>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
// Force frontend deployment Wed Oct  8 23:15:26 IST 2025
